import { useState, useEffect } from 'react';
import { Loader2, Settings2, CheckCircle2 } from 'lucide-react';

interface Preferences {
  karrierepasset: boolean;
  formal_education: boolean;
  certificates: boolean;
  career_history: boolean;
}

interface Props {
  onUpdated?: () => void;
}

const LABELS: Record<keyof Preferences, { title: string; description: string }> = {
  karrierepasset: { title: 'Personlighet og karriereinteresser', description: 'MBTI, persona og Holland-kode fra Karrierepasset' },
  formal_education: { title: 'Utdanning fra Vitnemålsportalen', description: 'Importerte vitnemål med ECTS, EQF/NKR' },
  certificates: { title: 'Kompetanseattester', description: 'Dine verifiserte kompetanseattester' },
  career_history: { title: 'Yrkeserfaring', description: 'Jobberfaring og yrkesroller (ESCO-koblet)' },
};

export function CvSectionPreferences({ onUpdated }: Props) {
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/cv/preferences', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (data.success) setPrefs(data.preferences);
      } catch {}
    })();
  }, []);

  async function toggle(key: keyof Preferences) {
    if (!prefs) return;
    const newPrefs = { ...prefs, [key]: !prefs[key] };
    setSaving(key);
    try {
      const res = await fetch('/api/cv/preferences', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPrefs),
      });
      const data = await res.json();
      if (data.success) {
        setPrefs(data.preferences);
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 2000);
        if (onUpdated) onUpdated();
      }
    } finally { setSaving(null); }
  }

  if (!prefs) return null;

  const activeCount = Object.values(prefs).filter(Boolean).length;

  return (
    <div className="bg-white rounded-lg shadow border border-kf-gray-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-4 flex items-center justify-between hover:bg-kf-gray-50 transition"
      >
        <div className="flex items-center gap-3">
          <Settings2 className="w-5 h-5 text-kf-blue-600" />
          <div className="text-left">
            <div className="font-semibold text-kf-gray-900">Tilpass CV</div>
            <div className="text-sm text-kf-gray-500">
              {activeCount} av 4 seksjoner vises i CV-en din
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showSaved && (
            <span className="inline-flex items-center gap-1 text-xs text-green-700">
              <CheckCircle2 className="w-3.5 h-3.5" /> Lagret
            </span>
          )}
          <span className="text-sm text-kf-gray-500">{open ? 'Skjul' : 'Vis'}</span>
        </div>
      </button>

      {open && (
        <div className="border-t border-kf-gray-100 p-4 space-y-3">
          {(Object.keys(LABELS) as Array<keyof Preferences>).map(key => (
            <label key={key} className="flex items-start gap-3 cursor-pointer hover:bg-kf-gray-50 p-2 rounded">
              <input
                type="checkbox"
                checked={prefs[key]}
                onChange={() => toggle(key)}
                disabled={saving === key}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-kf-gray-900 flex items-center gap-2">
                  {LABELS[key].title}
                  {saving === key && <Loader2 className="w-3.5 h-3.5 animate-spin text-kf-gray-400" />}
                </div>
                <div className="text-xs text-kf-gray-500">{LABELS[key].description}</div>
              </div>
            </label>
          ))}
          <p className="text-xs text-kf-gray-500 italic">
            Endringer påvirker både visning på skjerm og nedlastet PDF.
          </p>
        </div>
      )}
    </div>
  );
}
